const React = require('react');
const { createElement: h, useState, useEffect, useRef, useMemo } = React;
const dragData = require('../lib/dragData');
require('./WorkbookExplorerPanel.css');

var FILE_EXT = '.flw';

// All icons are outline-only (stroke), with cool colors via CSS currentColor.
function svgOutline(children, size) {
  var s = size || 16;
  return h('svg', {
    width: s,
    height: s,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.7,
    strokeLinecap: 'round',
    strokeLinejoin: 'round'
  }, children);
}

function chevronIcon() {
  return svgOutline([h('polyline', { key: 'p', points: '9 6 15 12 9 18' })], 12);
}

function folderIcon() {
  return svgOutline([
    h('path', { key: 'f', d: 'M3 7.5a2 2 0 012-2h3.6a2 2 0 011.4.6l1 1a2 2 0 001.4.6H19a2 2 0 012 2V17a2 2 0 01-2 2H5a2 2 0 01-2-2V7.5z' })
  ], 17);
}

function fileIcon() {
  return h('svg', {
    width: 20,
    height: 20,
    viewBox: '0 0 24 24',
    fill: 'none'
  },
    h('defs', null,
      h('linearGradient', { id: 'wbex-flow-grad', x1: '0%', y1: '50%', x2: '100%', y2: '50%' },
        h('stop', { offset: '0%', stopColor: '#7dd3fc' }),
        h('stop', { offset: '100%', stopColor: '#38bdf8' })
      )
    ),
    // Smooth S-wave from lower-left curving up to the arrow tip
    h('path', {
      d: 'M3 14 C 8 6, 14 14, 20 6',
      fill: 'none',
      stroke: 'url(#wbex-flow-grad)',
      strokeWidth: 2.4,
      strokeLinecap: 'round'
    }),
    // Arrow head pointing upper-right — vertex at (20, 6)
    h('polyline', {
      points: '15 6 20 6 20 11',
      fill: 'none',
      stroke: 'url(#wbex-flow-grad)',
      strokeWidth: 2.4,
      strokeLinecap: 'round',
      strokeLinejoin: 'round'
    })
  );
}

function plusIcon() {
  return svgOutline([h('path', { key: 'p', d: 'M12 5v14M5 12h14' })], 16);
}

function newFolderIcon() {
  return svgOutline([
    h('path', { key: 'p1', d: 'M3 7.5a2 2 0 012-2h3.6a2 2 0 011.4.6l1 1a2 2 0 001.4.6H19a2 2 0 012 2V17a2 2 0 01-2 2H5a2 2 0 01-2-2V7.5z' }),
    h('path', { key: 'p2', d: 'M12 11v5M9.5 13.5h5' })
  ], 16);
}

function moreIcon() {
  return svgOutline([
    h('circle', { key: 'c1', cx: 5, cy: 12, r: 1.2 }),
    h('circle', { key: 'c2', cx: 12, cy: 12, r: 1.2 }),
    h('circle', { key: 'c3', cx: 19, cy: 12, r: 1.2 })
  ], 16);
}

function buildFolderTree(folders) {
  var list = (Array.isArray(folders) ? folders : []).map(function(folder) {
    return {
      id: folder.id,
      name: folder.name,
      parentId: folder.parentId || null,
      order: folder.order || 0
    };
  });
  var byId = {};
  list.forEach(function(f) {
    byId[f.id] = Object.assign({}, f, { children: [], workflows: [] });
  });
  var roots = [];
  list.forEach(function(f) {
    var node = byId[f.id];
    if (f.parentId && byId[f.parentId]) byId[f.parentId].children.push(node);
    else roots.push(node);
  });
  function sort(arr) {
    arr.sort(function(a, b) {
      var byOrder = (a.order || 0) - (b.order || 0);
      if (byOrder !== 0) return byOrder;
      return String(a.name || '').localeCompare(String(b.name || ''));
    });
    arr.forEach(function(n) { sort(n.children); });
  }
  sort(roots);
  return { roots: roots, byId: byId };
}

function placeWorkflowsInTree(byId, workflows) {
  var roots = [];
  (workflows || []).forEach(function(wf) {
    if (wf.folderId && byId[wf.folderId]) byId[wf.folderId].workflows.push(wf);
    else roots.push(wf);
  });
  Object.keys(byId).forEach(function(key) {
    byId[key].workflows.sort(function(a, b) {
      return String(a.name || '').localeCompare(String(b.name || ''));
    });
  });
  roots.sort(function(a, b) { return String(a.name || '').localeCompare(String(b.name || '')); });
  return roots;
}

function ContextMenu(props) {
  var x = props.x;
  var y = props.y;
  var items = props.items || [];
  var onClose = props.onClose;
  var ref = useRef(null);

  useEffect(function() {
    function onDocClick(e) {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    }
    function onEsc(e) { if (e.key === 'Escape') onClose(); }
    var t = setTimeout(function() {
      document.addEventListener('mousedown', onDocClick);
    }, 0);
    document.addEventListener('keydown', onEsc);
    return function() {
      clearTimeout(t);
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [onClose]);

  return h('div', {
    ref: ref,
    className: 'wbex-context-menu',
    style: { left: x + 'px', top: y + 'px' }
  },
    items.map(function(item, idx) {
      if (item.separator) {
        return h('div', { key: 'sep-' + idx, className: 'wbex-context-menu__separator' });
      }
      return h('button', {
        key: item.label + '-' + idx,
        type: 'button',
        className: 'wbex-context-menu__item' + (item.danger ? ' wbex-context-menu__item--danger' : ''),
        onClick: function() {
          onClose();
          if (item.onClick) item.onClick();
        }
      }, item.label);
    })
  );
}

function WorkbookExplorerPanel(props) {
  var workbook = props.workbook || { folders: [], workflows: [], name: '' };
  var selectedWorkflowId = props.selectedWorkflowId || '';
  var onOpenWorkflow = props.onOpenWorkflow || function() {};
  var onCreateWorkflow = props.onCreateWorkflow || function() {};
  var onCreateFolder = props.onCreateFolder || function() {};
  var onRenameFolder = props.onRenameFolder || function() {};
  var onDeleteFolder = props.onDeleteFolder || function() {};
  var onDeleteWorkflow = props.onDeleteWorkflow || null;
  var onRenameWorkflow = props.onRenameWorkflow || null;
  var onRenameWorkbook = props.onRenameWorkbook || null;
  var onMoveWorkflow = props.onMoveWorkflow || function() {};
  var onMoveFolder = props.onMoveFolder || function() {};
  var onOpenMoveDialog = props.onOpenMoveDialog || null;
  var onConfirm = props.onConfirm || null; // ({ title, message, confirmLabel, danger, onConfirm }) => void
  // UI Pages
  var uiPages = props.uiPages || [];
  var onCreateUiPage = props.onCreateUiPage || null;
  var onOpenUiPage = props.onOpenUiPage || null;

  var [expandedIds, setExpandedIds] = useState({});
  var [editing, setEditing] = useState(null); // { kind: 'folder'|'workflow'|'workbook', id }
  var [contextMenu, setContextMenu] = useState(null);
  var [dropTargetId, setDropTargetId] = useState(null);
  var dragRef = useRef(null);

  // The parent can request inline-rename mode for an item it just created
  // (e.g. a new folder) by passing { kind, id, token }.
  var renameRequest = props.renameRequest || null;
  useEffect(function() {
    if (!renameRequest || !renameRequest.id) return;
    setEditing({ kind: renameRequest.kind || 'folder', id: renameRequest.id });
  }, [renameRequest]);

  var tree = useMemo(function() {
    var built = buildFolderTree(workbook.folders);
    var rootWorkflows = placeWorkflowsInTree(built.byId, workbook.workflows);
    return { roots: built.roots, rootWorkflows: rootWorkflows, byId: built.byId };
  }, [workbook]);

  function toggleFolder(id) {
    setExpandedIds(function(prev) {
      var next = Object.assign({}, prev);
      if (next[id]) delete next[id];
      else next[id] = true;
      return next;
    });
  }

  function startRenameFolder(folder) {
    setEditing({ kind: 'folder', id: folder.id });
  }

  function commitRenameFolder(folderId, value) {
    var name = String(value || '').trim();
    setEditing(null);
    if (!name) return;
    onRenameFolder(folderId, name);
  }

  function startRenameWorkflow(workflow) {
    setEditing({ kind: 'workflow', id: workflow.id });
  }

  function commitRenameWorkflow(workflowId, value) {
    var name = String(value || '').trim();
    setEditing(null);
    if (!name) return;
    if (typeof onRenameWorkflow === 'function') onRenameWorkflow(workflowId, name);
  }

  function startRenameWorkbook() {
    if (typeof onRenameWorkbook !== 'function') return;
    setEditing({ kind: 'workbook', id: workbook.id || '' });
  }

  function commitRenameWorkbook(value) {
    var name = String(value || '').trim();
    setEditing(null);
    if (!name) return;
    if (typeof onRenameWorkbook === 'function') onRenameWorkbook(name);
  }

  function requestConfirm(spec) {
    if (typeof onConfirm === 'function') {
      onConfirm(spec);
    } else if (spec && spec.onConfirm) {
      spec.onConfirm();
    }
  }

  function showFolderContextMenu(e, folder) {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      items: [
        { label: 'New flow here', onClick: function() {
          setExpandedIds(function(p) { var n = Object.assign({}, p); n[folder.id] = true; return n; });
          onCreateWorkflow(folder.id);
        } },
        { label: 'New folder', onClick: function() {
          setExpandedIds(function(p) { var n = Object.assign({}, p); n[folder.id] = true; return n; });
          onCreateFolder(folder.id);
        } },
        { separator: true },
        { label: 'Rename', onClick: function() { startRenameFolder(folder); } },
        onOpenMoveDialog ? { label: 'Move to…', onClick: function() { onOpenMoveDialog({ kind: 'folder', id: folder.id }); } } : null,
        { separator: true },
        { label: 'Delete folder', danger: true, onClick: function() {
          requestConfirm({
            title: 'Delete folder?',
            message: 'Delete "' + folder.name + '" and its subfolders. Flows inside will move to the playbook root.',
            confirmLabel: 'Delete',
            danger: true,
            onConfirm: function() { onDeleteFolder(folder.id); }
          });
        } }
      ].filter(Boolean)
    });
  }

  function showWorkflowContextMenu(e, workflow) {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      items: [
        { label: 'Open', onClick: function() { onOpenWorkflow(workflow.id); } },
        onRenameWorkflow ? { label: 'Rename', onClick: function() { startRenameWorkflow(workflow); } } : null,
        onOpenMoveDialog ? { label: 'Move to…', onClick: function() { onOpenMoveDialog({ kind: 'workflow', id: workflow.id }); } } : null,
        onDeleteWorkflow ? { separator: true } : null,
        onDeleteWorkflow ? { label: 'Delete flow', danger: true, onClick: function() {
          requestConfirm({
            title: 'Delete flow?',
            message: 'Delete "' + workflow.name + '". This cannot be undone.',
            confirmLabel: 'Delete',
            danger: true,
            onConfirm: function() { onDeleteWorkflow(workflow.id); }
          });
        } } : null
      ].filter(Boolean)
    });
  }

  // Drag-and-drop
  function onDragStartItem(kind, id, extra) {
    return function(e) {
      dragRef.current = { kind: kind, id: id };
      // 'move' inside the explorer (folder reorder); the canvas drop handler
      // overrides effect to 'copy' since dropping a flow on the canvas creates
      // a new flow-ref node and never removes the original file.
      try { e.dataTransfer.effectAllowed = 'copyMove'; } catch (err) {}
      // Internal folder reordering reads from dragRef (in-memory), so this
      // text/plain is just a compat redundancy.
      try { e.dataTransfer.setData('text/plain', kind + ':' + id); } catch (err) {}
      if (kind === 'workflow') {
        var payload = {
          id: String(id || ''),
          name: String((extra && extra.name) || '')
        };
        // Routes through the shared helper so the text/plain fallback format
        // matches what the canvas drop reader expects on older browsers that
        // reject custom MIME types. (Overrides the kind:id text above, which is
        // fine — reorder doesn't read it.)
        dragData.setData(e.dataTransfer, dragData.FLOW_TYPE, JSON.stringify(payload));
      }
    };
  }
  function onDragOverFolder(folderId) {
    return function(e) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      setDropTargetId(folderId || '__root__');
    };
  }
  function onDragLeave() { setDropTargetId(null); }
  function onDropFolder(folderId) {
    return function(e) {
      e.preventDefault();
      e.stopPropagation();
      var drag = dragRef.current;
      setDropTargetId(null);
      dragRef.current = null;
      if (!drag) return;
      if (drag.kind === 'workflow') {
        onMoveWorkflow(drag.id, folderId || '');
      } else if (drag.kind === 'folder' && drag.id !== folderId) {
        onMoveFolder(drag.id, folderId || null);
      }
    };
  }

  function renderEditableLabel(initialValue, onCommit, onCancel) {
    return h('input', {
      className: 'wbex-label-input',
      autoFocus: true,
      defaultValue: initialValue,
      onClick: function(e) { e.stopPropagation(); },
      onKeyDown: function(e) {
        if (e.key === 'Enter') { e.preventDefault(); onCommit(e.target.value); }
        else if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
      },
      onBlur: function(e) { onCommit(e.target.value); }
    });
  }

  function renderWorkflowRow(workflow, depth) {
    var isSelected = workflow.id === selectedWorkflowId;
    var displayName = String(workflow.name || 'Untitled') + FILE_EXT;
    var isEditing = editing && editing.kind === 'workflow' && editing.id === workflow.id;
    return h('div', {
      key: 'wf-' + workflow.id,
      className: 'wbex-row' + (isSelected ? ' wbex-row--selected' : ''),
      style: { paddingLeft: (8 + depth * 16) + 'px' },
      draggable: !isEditing,
      onDragStart: onDragStartItem('workflow', workflow.id, { name: workflow.name }),
      onClick: function() { if (!isEditing) onOpenWorkflow(workflow.id); },
      onContextMenu: function(e) { showWorkflowContextMenu(e, workflow); },
      title: displayName
    },
      h('span', { className: 'wbex-chevron wbex-chevron--placeholder' }),
      h('span', { className: 'wbex-icon wbex-icon--file' }, fileIcon()),
      isEditing
        ? renderEditableLabel(
            String(workflow.name || ''),
            function(value) { commitRenameWorkflow(workflow.id, value); },
            function() { setEditing(null); }
          )
        : h('span', { className: 'wbex-label' }, displayName),
      h('button', {
        type: 'button',
        className: 'wbex-row__menu-btn',
        onClick: function(e) { showWorkflowContextMenu(e, workflow); },
        title: 'Actions'
      }, moreIcon())
    );
  }

  function renderFolderRow(folder, depth) {
    var isOpen = !!expandedIds[folder.id];
    var isEditing = editing && editing.kind === 'folder' && editing.id === folder.id;
    var isDropTarget = dropTargetId === folder.id;
    var children = [];
    if (isOpen) {
      folder.children.forEach(function(child) { children.push(renderFolderRow(child, depth + 1)); });
      folder.workflows.forEach(function(wf) { children.push(renderWorkflowRow(wf, depth + 1)); });
    }
    return h(React.Fragment, { key: 'fd-' + folder.id },
      h('div', {
        className: 'wbex-row' + (isDropTarget ? ' wbex-row--drop-target' : ''),
        style: { paddingLeft: (6 + depth * 16) + 'px' },
        draggable: !isEditing,
        onDragStart: onDragStartItem('folder', folder.id),
        onDragOver: onDragOverFolder(folder.id),
        onDragLeave: onDragLeave,
        onDrop: onDropFolder(folder.id),
        onClick: function() { if (!isEditing) toggleFolder(folder.id); },
        onContextMenu: function(e) { showFolderContextMenu(e, folder); },
        title: folder.name
      },
        h('span', { className: 'wbex-chevron' + (isOpen ? ' wbex-chevron--open' : '') }, chevronIcon()),
        h('span', { className: 'wbex-icon wbex-icon--folder' }, folderIcon()),
        isEditing
          ? renderEditableLabel(folder.name, function(value) { commitRenameFolder(folder.id, value); }, function() { setEditing(null); })
          : h('span', { className: 'wbex-label' }, folder.name),
        h('button', {
          type: 'button',
          className: 'wbex-row__menu-btn',
          onClick: function(e) { showFolderContextMenu(e, folder); },
          title: 'Actions'
        }, moreIcon())
      ),
      children
    );
  }

  var rootDropClass = dropTargetId === '__root__' ? ' wbex-tree--drop-target' : '';

  return h('div', { className: 'wbex-panel' },
    h('div', { className: 'wbex-header wbex-header--no-title' },
      h('div', { className: 'wbex-header__actions' },
        h('button', {
          type: 'button',
          className: 'wbex-icon-btn',
          title: 'New flow',
          onClick: function(e) { e.stopPropagation(); onCreateWorkflow(''); }
        }, plusIcon()),
        h('button', {
          type: 'button',
          className: 'wbex-icon-btn',
          title: 'New folder',
          onClick: function(e) { e.stopPropagation(); onCreateFolder(null); }
        }, newFolderIcon()),
      )
    ),
    h('div', { className: 'wbex-divider' }),
    h('div', {
      className: 'wbex-tree' + rootDropClass,
      onDragOver: onDragOverFolder(null),
      onDragLeave: onDragLeave,
      onDrop: onDropFolder(null)
    },
      tree.roots.length === 0 && tree.rootWorkflows.length === 0 && uiPages.length === 0
        ? h('div', { className: 'wbex-empty' }, 'Nothing here yet.', h('br'), 'Click + to add a flow, folder, or UI page.')
        : null,
      tree.roots.map(function(folder) { return renderFolderRow(folder, 0); }),
      tree.rootWorkflows.map(function(wf) { return renderWorkflowRow(wf, 0); }),
      // UI Pages — flat list at the end of the root
      uiPages.length > 0
        ? h('div', { key: 'ui-pages-section', className: 'wbex-ui-pages-section' },
            h('div', { className: 'wbex-ui-pages-header', style: { padding: '8px 12px 4px', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', color: '#94a3b8', fontWeight: 600 } }, 'UI Pages'),
            uiPages.map(function(page) {
              return h('div', {
                key: page.id,
                className: 'wbex-row wbex-row--workflow',
                onClick: function() { if (onOpenUiPage) onOpenUiPage(page.id); },
                style: { cursor: 'pointer' }
              },
                h('span', { className: 'wbex-icon wbex-icon--workflow' },
                  h('svg', {
                    width: 16, height: 16, viewBox: '0 0 24 24',
                    fill: 'none', stroke: '#3b82f6', strokeWidth: 1.7,
                    strokeLinecap: 'round', strokeLinejoin: 'round'
                  },
                    h('rect', { x: 3, y: 3, width: 18, height: 18, rx: 2 }),
                    h('line', { x1: 3, y1: 9, x2: 21, y2: 9 })
                  )
                ),
                h('span', { className: 'wbex-label' }, page.name || 'Untitled page'),
                page.isPublic
                  ? h('span', { style: { fontSize: '10px', color: '#22c55e', marginLeft: '6px' }, title: 'Published' }, '●')
                  : null
              );
            })
          )
        : null
    ),
    contextMenu
      ? h(ContextMenu, {
          x: contextMenu.x,
          y: contextMenu.y,
          items: contextMenu.items,
          onClose: function() { setContextMenu(null); }
        })
      : null
  );
}

WorkbookExplorerPanel.buildFolderTree = buildFolderTree;
WorkbookExplorerPanel.FILE_EXT = FILE_EXT;
module.exports = WorkbookExplorerPanel;
